import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '../lib/cn'

export type BadgeTone = 'neutral' | 'brand' | 'accent' | 'verified' | 'warning' | 'critical' | 'info'

const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-surface-inset text-text-secondary border-border-subtle',
  brand: 'bg-surface-selected text-brand-primary border-border-strong',
  accent: 'bg-surface-inset text-brand-accent border-border-subtle',
  verified: 'bg-state-verified-surface text-state-verified border-state-verified-border',
  warning: 'bg-state-warning-surface text-state-warning border-state-warning-border',
  critical: 'bg-state-critical-surface text-state-critical border-state-critical-border',
  info: 'bg-state-info-surface text-state-info border-state-info-border',
}

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone
  icon?: ReactNode
}

/**
 * Static label. Note there is deliberately no `ai` tone here: AI provenance is
 * `<AIBadge>` in @insightai/ai-patterns, which cannot render without a model
 * source. Allowing a decorative violet badge in the generic primitive is how the
 * AI-transparency signal gets diluted (§4.1 rule 1).
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { className, tone = 'neutral', icon, children, ...rest },
  ref,
) {
  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-caption font-medium',
        TONES[tone],
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </span>
  )
})
